// src/pages/dashboard/ProgressDashboard.jsx

import { sanityClient } from "../../lib/sanity/sanityClient";

import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";

import {
  getLevelResumeProgress,
  buildLevelResumePath,
} from "../../lib/learning/levelResumeProgress";

import {

  ArrowRight,

  CalendarDays,

  Check,

  ClipboardCheck,

  Flame,

  Lock,

  Star,

  TrendingUp,

  Trophy,

  Zap,

} from "lucide-react";



import InstallPrompt from "../../components/pwa/InstallPrompt";
import Banner from "../../components/layout/Banner";

import AppScreenShell from "../../components/layout/AppScreenShell";

import LevelAccessBlockedModal from "../../components/learning/LevelAccessBlockedModal";

import SubscriptionPopup from "../../components/subscriptions/SubscriptionPopup";
import LearningFeedbackModal from "../../components/feedback/LearningFeedbackModal";
import {
  FEEDBACK_STATUSES,
  recordLearningFeedback,
} from "../../lib/feedback/learningFeedback";
import {
  POST_LEVEL_POPUP_TYPES,
  getPostLevelPopupQueue,
  removePostLevelPopup,
} from "../../lib/learning/postLevelPopups";

import WeeklyFinancialCheckIn from "../../components/check-ins/WeeklyFinancialCheckIn";



import {
  getCurrentSAWeekStart,
  getMyWeeklyFinancialCheckIns,
  getOrStartMyWeeklyCheckInTracking,
  getWeeklyCheckInStatus,
  getWeeklyCheckInSummary,
} from "../../lib/check-ins/weeklyCheckIn";






import { supabase } from "../../supabaseClient";
import {
  completeMyActionOccurrence,
  getMyActions,
  getMyCalendarReminders,
  refreshMyFinancialActions,
} from "../../lib/actions/calendarSetup";

import { getUserTotalXp } from "../../lib/learning/lessons";

import {

  getCurrentUserStreakSummary,

  recordCurrentUserStreakActivity,

} from "../../lib/learning/streaks";

import { checkLevelAccessForCurrentUser } from "../../lib/learning/levelAccess";

import { checkPaidJourneyAccessForCurrentUser } from "../../lib/access/paidJourneyAccess";



const PENGUIN_BUILDER_SRC =

  "https://kwbgrjakobarqelunebd.supabase.co/storage/v1/object/public/pictures/MVP4/In-App/Gwin-Builder1.png";




const RANKS = [

  {

    id: "starter",

    name: "Money Starter",

    tagline: "You are building your foundation.",

    min: 0,

    max: 3150,

    icon: Star,

  },

  {

    id: "builder",

    name: "Money Builder",

    tagline: "You are gaining momentum.",

    min: 3151,

    max: 6750,

    icon: TrendingUp,

  },

  {

    id: "planner",

    name: "Money Strategist",

    tagline: "You are learning to make better business decisions.",

    min: 6751,

    max: 9900,

    icon: ClipboardCheck,

  },

  {

    id: "funding-ready",

    name: "Money Boss",

    tagline: "You are preparing for serious funding conversations.",

    min: 9901,

    max: 15750,

    icon: Trophy,

  },

];



const DEFAULT_STATS = {

  userId: null,

  displayName: "User",

  totalXp: 0,

  levelsCompleted: 0,

  totalLevels: 0,

  streakValue: 0,

  longestStreak: 0,

  lastActivityDate: "—",

  weeklyLevels: [],



  previousLevel: null,



  continueLevel: {

    title: "Continue your learning journey",

    status: "available",

    to: "/journey",

    questId: "1",

    subquestId: "",

    levelKey: "",

  },



  nextLevel: null,

};



function getRank(totalXp) {

  return (

    RANKS.find(

      (rank) =>

        totalXp >= rank.min &&

        totalXp <= rank.max

    ) || RANKS[0]

  );

}



function getNextRank(totalXp) {

  return (

    RANKS.find((rank) => totalXp < rank.min) ||

    null

  );

}



function xpToNextRank(totalXp) {

  const nextRank = getNextRank(totalXp);



  if (!nextRank) {

    return {

      needed: 0,

      progress: 100,

    };

  }



  const currentRank = getRank(totalXp);



  const span =

    nextRank.min - currentRank.min;



  const earned =

    totalXp - currentRank.min;



  return {

    needed: Math.max(

      0,

      nextRank.min - totalXp

    ),



    progress: Math.max(

      0,

      Math.min(

        100,

        Math.round((earned / span) * 100)

      )

    ),

  };

}



function normalizeQuestKey(qid) {

  if (!qid) return "1";



  if (

    qid === "financial-foundations" ||

    qid === "quest-1" ||

    qid === "Q1"

  ) {

    return "1";

  }



  if (qid === "quest-2" || qid === "Q2") {

    return "2";

  }



  if (qid === "quest-3" || qid === "Q3") {

    return "3";

  }



  if (qid === "quest-4" || qid === "Q4") {

    return "4";

  }



  if (qid === "quest-5" || qid === "Q5") {

    return "5";

  }



  return String(qid);

}



function normalizeLevelKey(levelKey) {

  if (!levelKey) return "L1";



  const value = String(levelKey).trim();



  if (/^L\d+$/i.test(value)) {

    return value.toUpperCase();

  }



  if (/^\d+$/.test(value)) {

    return `L${value}`;

  }



  const match = value.match(/(\d+)/);



  return match

    ? `L${match[1]}`

    : value.toUpperCase();

}



function parseLevelIndex(levelKey) {

  const match = String(

    levelKey || ""

  ).match(/(\d+)/);



  const number = Number.parseInt(

    match?.[1] || "",

    10

  );



  return Number.isFinite(number)

    ? number

    : 999;

}



function getQuestNumber(value) {

  const normalized =

    normalizeQuestKey(value);



  const number = Number.parseInt(

    normalized,

    10

  );



  return Number.isFinite(number)

    ? number

    : null;

}



function requiresJourneyAccessCheck(questId) {
  return !!getQuestNumber(questId);
}

function parseSubquestIndex(subquestId) {

  const match = String(

    subquestId || ""

  ).match(/(\d+)$/);



  const number = Number.parseInt(

    match?.[1] || "",

    10

  );



  return Number.isFinite(number)

    ? number

    : 999;

}



function makeLevelKey(questId, subquestId, levelKey) {
  // Level keys are currently unique in Supabase.
  // Legacy route identifiers are no longer used for matching.
  return normalizeLevelKey(levelKey);
}



function sortLessonRows(rows = []) {

  return [...rows].sort((a, b) => {

    const aq =

      Number(

        normalizeQuestKey(a.quest_id)

      ) || 0;



    const bq =

      Number(

        normalizeQuestKey(b.quest_id)

      ) || 0;



    if (aq !== bq) {

      return aq - bq;

    }



    const as = parseSubquestIndex(

      a.subquest_id

    );



    const bs = parseSubquestIndex(

      b.subquest_id

    );



    if (as !== bs) {

      return as - bs;

    }



    return (

      parseLevelIndex(a.level_key) -

      parseLevelIndex(b.level_key)

    );

  });

}



function groupLessonsIntoLevels(

  lessons = [],

  progressByLessonId = new Map()

) {

  const grouped = new Map();



  lessons.forEach((lesson) => {

    const questId =

      normalizeQuestKey(

        lesson.quest_id

      );



    const subquestId = String(

      lesson.subquest_id || ""

    );



    const levelKey =

      normalizeLevelKey(

        lesson.level_key

      );



    if (

      !questId ||

      !subquestId ||

      !levelKey

    ) {

      return;

    }



    const key = makeLevelKey(

      questId,

      subquestId,

      levelKey

    );



    const progressRow =

      progressByLessonId.get(

        String(lesson.id)

      );



    if (!grouped.has(key)) {

      grouped.set(key, {

        key,

        quest_id: questId,

        subquest_id: subquestId,

        level_key: levelKey,

        title: lesson.title || "",

        totalLessons: 0,

        completedLessons: 0,

        started: false,

        complete: false,

        firstLesson: lesson,

      });

    }



    const group = grouped.get(key);



    group.totalLessons += 1;



    const lessonComplete =
  progressRow?.completed === true;



    const lessonStarted =

      !!progressRow &&

      Number(

        progressRow?.xp_earned || 0

      ) > 0;



    if (lessonComplete) {

      group.completedLessons += 1;

    }



    if (lessonStarted) {

      group.started = true;

    }



    if (

      !group.title &&

      lesson.title

    ) {

      group.title =

        lesson.title;

    }

  });



  return Array.from(

    grouped.values()

  ).map((level) => ({

    ...level,



    complete:

      level.totalLessons > 0 &&

      level.completedLessons >=

        level.totalLessons,

  }));

}



function buildPlayRouteFromLesson(

  lesson

) {

  if (!lesson) {

    return "/journey";

  }



  const questId =

    normalizeQuestKey(

      lesson.quest_id

    );



  const subquestId = String(

    lesson.subquest_id || ""

  );



  const levelKey =

    normalizeLevelKey(

      lesson.level_key

    );



  if (

    !questId ||

    !subquestId ||

    !levelKey

  ) {

    return "/journey";

  }



  return `/play/${questId}/${subquestId}/${levelKey}`;

}



function makeDashboardLevel(level) {

  if (!level) {

    return null;

  }



  return {

    title:

      level.title ||

      `Level ${parseLevelIndex(

        level.level_key

      )}`,



    status: level.complete

      ? "complete"

      : level.started

      ? "in_progress"

      : "available",



    questId:

      normalizeQuestKey(

        level.quest_id

      ),



    subquestId: String(

      level.subquest_id || ""

    ),



    levelKey:

      normalizeLevelKey(

        level.level_key

      ),



    to: buildPlayRouteFromLesson({

      quest_id: level.quest_id,

      subquest_id:

        level.subquest_id,

      level_key:

        level.level_key,

    }),

  };

}



async function resolveDisplayName(user) {

  const metaName = String(

    user?.user_metadata?.full_name ||

      ""

  ).trim();



  let databaseName = "";



  try {

    const {

      data,

      error,

    } = await supabase

      .from("user")

      .select("full_name")

      .eq("id", user.id)

      .maybeSingle();



    if (!error) {

      databaseName = String(

        data?.full_name || ""

      ).trim();

    }

  } catch (error) {

    console.warn(

      "Dashboard name could not be loaded:",

      error

    );

  }



  if (databaseName) {

    return databaseName;

  }



  if (metaName) {

    return metaName;

  }



  const emailName = String(

    user?.email || ""

  )

    .split("@")[0]

    .replace(/[._-]+/g, " ")

    .replace(/\b\w/g, (character) =>

      character.toUpperCase()

    );



  return emailName || "User";

}

async function fetchSanityLevelDraftLessons() {
  const query = `
    *[
      _type == "levelDraft" &&
      defined(levelKey)
    ] | order(levelOrder asc) {
      _id,
      title,
      levelKey,
      levelOrder,
      status
    }
  `;

  try {
    const drafts = (await sanityClient.fetch(query)) || [];

    return drafts
      .map((level) => {
        const levelKey = normalizeLevelKey(level.levelKey);

        if (!levelKey) {
          return null;
        }

        return {
          id: `sanity-level-draft-${level._id}`,
          title: level.title || `Level ${parseLevelIndex(levelKey)}`,
          quest_id: "1",
          subquest_id: "1-1",
          level_key: levelKey,
          level_order: level.levelOrder || parseLevelIndex(levelKey),
          source: "levelDraft",
          status: level.status || "draft",
        };
      })
      .filter(Boolean);
  } catch (error) {
    console.warn("Sanity level drafts could not be loaded:", error);
    return [];
  }
}

async function loadProgressDashboardData() {

  const {

    data: authData,

  } = await supabase.auth.getUser();



  const user =

    authData?.user;



  if (!user) {

    return DEFAULT_STATS;

  }



  try {

    await recordCurrentUserStreakActivity({

      countLogin: true,

      countLesson: false,

    });

  } catch (error) {

    console.warn(

      "Streak activity could not be recorded:",

      error

    );

  }



  const [
  displayName,
  totalXpResult,
  streakResult,
  lessonsRes,
  progressRes,
  sanityDraftLessons,
] = await Promise.all([
  resolveDisplayName(user),

  getUserTotalXp(supabase),

  getCurrentUserStreakSummary(),

  supabase
    .from("lessons")
    .select("id, title, quest_id, subquest_id, level_key"),

  supabase
    .from("lesson_progress")
    .select("*")
    .eq("user_id", user.id),

  fetchSanityLevelDraftLessons(),
]);



  const totalXp =

    Number(totalXpResult) || 0;



  const streakValue =

    Number(

      streakResult?.current_streak

    ) || 0;



  const longestStreak =

    Number(

      streakResult?.longest_streak ??

        streakResult?.longestStreak ??

        streakResult?.longest_streak_count

    ) || 0;



  const lastActivityDate =

    streakResult?.last_activity_date ||

    streakResult?.lastActivityDate ||

    streakResult?.last_activity ||

    "—";



  // Supabase lesson IDs are the source of truth for completion and XP.
  // Sanity supplies the displayed title and any new level placeholders.
  const supabaseLessons = lessonsRes.data || [];
  const sanityLevels = Array.isArray(sanityDraftLessons)
    ? sanityDraftLessons
    : [];

  const sanityByLevelKey = new Map(
    sanityLevels.map((level) => [
      makeLevelKey(level.quest_id, level.subquest_id, level.level_key),
      level,
    ])
  );

  const existingLevelKeys = new Set(
    supabaseLessons.map((lesson) =>
      makeLevelKey(lesson.quest_id, lesson.subquest_id, lesson.level_key)
    )
  );

  const lessons = [
    ...supabaseLessons.map((lesson) => {
      const sanityLevel = sanityByLevelKey.get(
        makeLevelKey(lesson.quest_id, lesson.subquest_id, lesson.level_key)
      );

      return {
        ...lesson,
        title: sanityLevel?.title || lesson.title,
      };
    }),
    // Show newly authored Sanity levels even before a corresponding
    // Supabase lesson exists. Progress for these levels must eventually
    // be persisted against a real Supabase lesson ID.
    ...sanityLevels.filter(
      (level) =>
        !existingLevelKeys.has(
          makeLevelKey(level.quest_id, level.subquest_id, level.level_key)
        )
    ),
  ];



  const progress =

    progressRes.data || [];



  if (lessonsRes.error) {

    console.warn(

      "Lessons could not be loaded:",

      lessonsRes.error.message

    );

  }



  if (progressRes.error) {

    console.warn(

      "Lesson progress could not be loaded:",

      progressRes.error.message

    );

  }



  const progressByLessonId =

    new Map(

      progress.map((row) => [

        String(row.lesson_id),

        row,

      ])

    );



  const sortedLessons =

    sortLessonRows(lessons);



  const sortedLevels =

    groupLessonsIntoLevels(

      sortedLessons,

      progressByLessonId

    ).sort((a, b) => {

      const aq =

        Number(

          normalizeQuestKey(

            a.quest_id

          )

        ) || 0;



      const bq =

        Number(

          normalizeQuestKey(

            b.quest_id

          )

        ) || 0;



      if (aq !== bq) {

        return aq - bq;

      }



      const as =

        parseSubquestIndex(

          a.subquest_id

        );



      const bs =

        parseSubquestIndex(

          b.subquest_id

        );



      if (as !== bs) {

        return as - bs;

      }



      return (

        parseLevelIndex(

          a.level_key

        ) -

        parseLevelIndex(

          b.level_key

        )

      );

    });



  const levelsCompleted =

    sortedLevels.filter(

      (level) => level.complete

    ).length;



  const totalLevels =

    sortedLevels.length;



  const inProgressLevel =

    sortedLevels.find(

      (level) =>

        level.started &&

        !level.complete

    ) || null;



  const firstIncompleteLevel =

    sortedLevels.find(

      (level) =>

        !level.complete

    ) || null;



  const targetLevel =

    inProgressLevel ||

    firstIncompleteLevel;



  let targetIndex = -1;



  if (targetLevel) {

    targetIndex =

      sortedLevels.findIndex(

        (level) =>

          level.key ===

          targetLevel.key

      );

  }



  const previousLevel =

    targetIndex > 0

      ? makeDashboardLevel(

          sortedLevels[

            targetIndex - 1

          ]

        )

      : null;



  const nextLevel =

    targetIndex >= 0 &&

    targetIndex <

      sortedLevels.length - 1

      ? makeDashboardLevel(

          sortedLevels[

            targetIndex + 1

          ]

        )

      : null;



  const continueLevel =

    targetLevel

      ? makeDashboardLevel(

          targetLevel

        )

      : {

          title:

            "Go to your learning journey",



          status:

            "available",



          to: "/journey",



          questId: "1",



          subquestId: "",



          levelKey: "",

        };


        const firstIncompleteIndex =
  sortedLevels.findIndex(
    (level) => !level.complete
  );

const weeklyLevelStartIndex =
  firstIncompleteIndex >= 0
    ? Math.floor(
        firstIncompleteIndex / 3
      ) * 3
    : Math.max(
        0,
        sortedLevels.length - 3
      );

const weeklyLevels =
  sortedLevels
    .slice(
      weeklyLevelStartIndex,
      weeklyLevelStartIndex + 3
    )
    .map((level) =>
      makeDashboardLevel(level)
    );

  return {
  userId: user.id,
  displayName,
  totalXp,
  levelsCompleted,
  totalLevels,
  streakValue,
  longestStreak,
  lastActivityDate,
  previousLevel,
  continueLevel,
  nextLevel,
  weeklyLevels,
};

}



export default function ProgressDashboard() {
console.log("[GWIN TEST] ProgressDashboard is rendering");
  const navigate =

    useNavigate();


    
const [weeklyCheckInOpen, setWeeklyCheckInOpen] =
  useState(false);

  // The reporting week rolls over automatically each Monday in South Africa.
  const activeWeeklyWeekStart = getCurrentSAWeekStart();

const [weeklyCheckInStatus, setWeeklyCheckInStatus] =
  useState("loading");

const [previousWeekMissed, setPreviousWeekMissed] =
  useState(false);


const [weeklyCheckInSummary, setWeeklyCheckInSummary] =
  useState({
    completed: 0,
    missed: 0,
    total: 0,
    progress: 0,
  });


  const [

    loadingStats,

    setLoadingStats,

  ] = useState(true);



  const [

    refreshKey,

    setRefreshKey,

  ] = useState(0);

  // Refresh the dashboard after a South African calendar-day change,
  // including when the user returns to an inactive browser tab.
  useEffect(() => {
    const getSADateKey = () => {
      const parts = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Africa/Johannesburg",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).formatToParts(new Date());

      const part = (type) =>
        parts.find((item) => item.type === type)?.value;

      return `${part("year")}-${part("month")}-${part("day")}`;
    };

    let lastDate = getSADateKey();
    const checkForDateChange = () => {
      const today = getSADateKey();
      if (today !== lastDate) {
        lastDate = today;
        setRefreshKey((current) => current + 1);
      }
    };

    const interval = window.setInterval(checkForDateChange, 60 * 1000);
    const onVisibilityChange = () => {
      if (!document.hidden) checkForDateChange();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", checkForDateChange);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", checkForDateChange);
    };
  }, []);

  const [

    checkingContinue,

    setCheckingContinue,

  ] = useState(false);



  const [

    levelAccessBlock,

    setLevelAccessBlock,

  ] = useState(null);



  const [

    showFreeQuestCompleteModal,

    setShowFreeQuestCompleteModal,

  ] = useState(false);



  const [

    showSubscriptionPopup,

    setShowSubscriptionPopup,

  ] = useState(false);



  const [

    paidJourneyAccess,

    setPaidJourneyAccess,

  ] = useState({

    checked: false,

    allowed: false,

  });



  const [

    paidJourneyAccessLoading,

    setPaidJourneyAccessLoading,

  ] = useState(false);



  const [

    stats,

    setStats,

  ] = useState(

    DEFAULT_STATS

  );

  const [
  dashboardActions,
  setDashboardActions,
] = useState([]);

const [
  dashboardActionsLoading,
  setDashboardActionsLoading,
] = useState(true);

  const [
    postLevelPopupQueue,
    setPostLevelPopupQueue,
  ] = useState([]);

  const [
    postLevelFeedbackSaving,
    setPostLevelFeedbackSaving,
  ] = useState(false);

  const [
    postLevelFeedbackError,
    setPostLevelFeedbackError,
  ] = useState("");


  const [
  completingActionId,
  setCompletingActionId,
] = useState(null);




  useEffect(() => {

    let active = true;



    async function run() {

      try {

        setLoadingStats(true);



        const nextStats =

          await loadProgressDashboardData();



        if (!active) {

          return;

        }



        setStats(nextStats);

      } catch (error) {

        console.warn(

          "Progress dashboard stats could not be loaded:",

          error

        );



        if (!active) {

          return;

        }



        setStats(

          DEFAULT_STATS

        );

      } finally {

        if (active) {

          setLoadingStats(

            false

          );

        }

      }

    }

    



    run();



    return () => {

      active = false;

    };

  }, [refreshKey]);

  async function handleCompleteAction(action) {
  if (
    !action?.id ||
    completingActionId
  ) {
    return;
  }

  try {
    setCompletingActionId(action.id);

    await completeMyActionOccurrence(
      action.id
    );

    // Month-end completion can unlock further financial actions.
    // Refresh is optional: existing calendar records must still load
// even when a generator encounters an error.
try {
  await refreshMyFinancialActions();
} catch (refreshError) {
  console.warn(
    "Financial calendar refresh failed:",
    refreshError
  );
}

const [actionsResult, remindersResult] =
  await Promise.allSettled([
    getMyActions(),
    getMyCalendarReminders(),
  ]);

if (actionsResult.status === "rejected") {
  console.warn(
    "Could not load calendar setup action:",
    actionsResult.reason
  );
}

if (remindersResult.status === "rejected") {
  console.error(
    "Could not load calendar reminders:",
    remindersResult.reason
  );
}

const actionRows =
  actionsResult.status === "fulfilled"
    ? actionsResult.value
    : [];

const reminderRows =
  remindersResult.status === "fulfilled"
    ? remindersResult.value
    : [];

const setupRows = (actionRows || []).filter(
  (action) =>
    action.definition_code === "SYSTEM_CALENDAR_SETUP" ||
    action.code === "SYSTEM_CALENDAR_SETUP" ||
    action.action_type === "calendar_setup"
);

setDashboardActions([
  ...setupRows,
  ...(reminderRows || []),
]);
  } catch (error) {
    console.error(
      "Action could not be completed:",
      error
    );
  } finally {
    setCompletingActionId(null);
  }
}



  const {
  userId,
  displayName,
  totalXp,
  levelsCompleted,
  totalLevels,
  streakValue,
  longestStreak,
  previousLevel,
  continueLevel,
  nextLevel,
  weeklyLevels = [],
} = stats;



useEffect(() => {
  let alive = true;

  async function loadWeeklyCheckInStatus() {
    if (!userId) {
      setWeeklyCheckInStatus("loading");
      setPreviousWeekMissed(false);
      setWeeklyCheckInSummary({
        completed: 0,
        missed: 0,
        total: 0,
        progress: 0,
      });
      return;
    }

    try {
      setWeeklyCheckInStatus("loading");

      const [trackingStartWeek, submissions] =
        await Promise.all([
          getOrStartMyWeeklyCheckInTracking(),
          getMyWeeklyFinancialCheckIns(),
        ]);

      if (!alive) return;

      const now = new Date();

const currentWeek = getCurrentSAWeekStart(now);

      const previousDate = new Date(
        `${currentWeek}T12:00:00Z`
      );

      previousDate.setUTCDate(
        previousDate.getUTCDate() - 7
      );

      const previousWeek =
        previousDate.toISOString().slice(0, 10);

      const currentSubmission =
        submissions.find(
          (row) => row.week_start === currentWeek
        ) || null;

      const previousSubmission =
        submissions.find(
          (row) => row.week_start === previousWeek
        ) || null;

      setWeeklyCheckInStatus(
        getWeeklyCheckInStatus(
          currentWeek,
          currentSubmission,
          now
        )
      );

      setPreviousWeekMissed(
        Boolean(
          trackingStartWeek &&
          previousWeek >= trackingStartWeek &&
          getWeeklyCheckInStatus(
            previousWeek,
            previousSubmission,
            now
          ) === "missed"
        )
      );

      setWeeklyCheckInSummary(
        getWeeklyCheckInSummary(
          submissions,
          trackingStartWeek,
          now
        )
      );
    } catch (error) {
      console.error(
        "Could not load weekly financial check-ins:",
        error
      );

      if (alive) {
        setWeeklyCheckInStatus("error");
        setPreviousWeekMissed(false);
      }
    }
  }

  loadWeeklyCheckInStatus();

  return () => {
    alive = false;
  };
}, [userId, refreshKey]);




  useEffect(() => {
  let alive = true;

  async function loadDashboardActions() {
  console.log("[GWIN TEST] Loading dashboard actions", {
    userId,
  });

  if (!userId) {
    if (alive) {
      setDashboardActions([]);
      setDashboardActionsLoading(false);
    }

    return;
  }

    try {
  setDashboardActionsLoading(true);

  // Refresh the generated financial actions.
  // If this fails, still load the saved calendar records.
  try {
    await refreshMyFinancialActions();
  } catch (refreshError) {
    console.warn(
      "Financial calendar refresh failed:",
      refreshError
    );
  }

  // Load the setup action and calendar reminders independently.
  const [actionsResult, remindersResult] =
    await Promise.allSettled([
      getMyActions(),
      getMyCalendarReminders(),
    ]);

  if (actionsResult.status === "rejected") {
    console.warn(
      "Could not load calendar setup action:",
      actionsResult.reason
    );
  }

  if (remindersResult.status === "rejected") {
    console.error(
      "Could not load calendar reminders:",
      remindersResult.reason
    );
  }

  const actionRows =
    actionsResult.status === "fulfilled"
      ? actionsResult.value
      : [];

  const reminderRows =
    remindersResult.status === "fulfilled"
      ? remindersResult.value
      : [];

  console.log("Dashboard calendar data:", {
    setupActions: actionRows.length,
    reminders: reminderRows.length,
  });

  const setupRows = actionRows.filter(
    (action) =>
      action.definition_code === "SYSTEM_CALENDAR_SETUP" ||
      action.code === "SYSTEM_CALENDAR_SETUP" ||
      action.action_type === "calendar_setup"
  );

  const rows = [...setupRows, ...reminderRows];

  if (!alive) return;

  setDashboardActions(rows);
} catch (error) {
      console.warn(
        "Dashboard actions could not be loaded:",
        error
      );

      if (alive) {
        setDashboardActions([]);
      }
    } finally {
      if (alive) {
        setDashboardActionsLoading(false);
      }
    }
  }

  loadDashboardActions();

  return () => {
    alive = false;
  };
}, [
  userId,
  refreshKey,
]);


  const refreshPostLevelPopupQueue =
    useCallback(() => {
      if (!userId) {
        setPostLevelPopupQueue([]);
        return;
      }

      setPostLevelPopupQueue(
        getPostLevelPopupQueue(
          userId
        )
      );
    }, [userId]);

  useEffect(() => {
    refreshPostLevelPopupQueue();
  }, [refreshPostLevelPopupQueue]);




  const continueRequiresPaidAccess =

    requiresJourneyAccessCheck(

      continueLevel?.questId,

      continueLevel?.levelKey

    );



  const continueLevelLocked =

    continueRequiresPaidAccess &&

    paidJourneyAccess.checked &&

    paidJourneyAccess.allowed !==

      true;



  useEffect(() => {

    let active = true;



    async function loadPaidAccess() {

      if (

        !continueRequiresPaidAccess

      ) {

        setPaidJourneyAccess({

          checked: true,

          allowed: true,

        });



        return;

      }



      try {

        setPaidJourneyAccessLoading(

          true

        );



        const result =

          await checkPaidJourneyAccessForCurrentUser(

            {

              questId:

                getQuestNumber(

                  continueLevel?.questId

                ),



              levelNumber:

                parseLevelIndex(

                  continueLevel?.levelKey

                ),

            }

          );



        if (!active) {

          return;

        }



        setPaidJourneyAccess({

          checked: true,

          ...result,

        });

      } catch (error) {

        console.warn(

          "Paid journey access could not be checked:",

          error

        );



        if (!active) {

          return;

        }



        setPaidJourneyAccess({

          checked: true,

          allowed: false,

          reason:

            "access_check_failed",

          message:

            "We could not check your journey access right now. Please try again.",

        });

      } finally {

        if (active) {

          setPaidJourneyAccessLoading(

            false

          );

        }

      }

    }



    loadPaidAccess();



    return () => {

      active = false;

    };

  }, [

    continueRequiresPaidAccess,

    continueLevel?.questId,

    continueLevel?.levelKey,

    refreshKey,

  ]);



  const rank =

    useMemo(

      () =>

        getRank(totalXp),

      [totalXp]

    );



  const nextRank =

    useMemo(

      () =>

        getNextRank(totalXp),

      [totalXp]

    );



  const toNext =

    useMemo(

      () =>

        xpToNextRank(totalXp),

      [totalXp]

    );



  
async function handleContinueClick() {
  if (
    loadingStats ||
    checkingContinue ||
    paidJourneyAccessLoading
  ) {
    return;
  }

  if (
    !continueLevel?.questId ||
    !continueLevel?.subquestId ||
    !continueLevel?.levelKey ||
    !continueLevel?.to ||
    continueLevel.to === "/dashboard"
  ) {
    navigate("/journey");
    return;
  }

  try {
    setCheckingContinue(true);

    // 1. Preserve existing paid journey access checks.
    if (continueRequiresPaidAccess) {
      const access =
        paidJourneyAccess.checked === true
          ? paidJourneyAccess
          : await checkPaidJourneyAccessForCurrentUser({
              questId: getQuestNumber(continueLevel.questId),
              levelNumber: parseLevelIndex(continueLevel.levelKey),
            });

      setPaidJourneyAccess({
        checked: true,
        ...access,
      });

      if (!access.allowed) {
        setShowFreeQuestCompleteModal(true);
        return;
      }
    }

    // 2. Preserve existing level access checks.
    const access = await checkLevelAccessForCurrentUser({
      questId: continueLevel.questId,
      subquestId: continueLevel.subquestId,
      levelKey: continueLevel.levelKey,
    });

    if (!access.allowed) {
      setLevelAccessBlock(access);
      return;
    }

    // 3. Retrieve saved resume progress from Supabase.
    const savedResume = await getLevelResumeProgress({
      userId,
      questId: continueLevel.questId,
      subquestId: continueLevel.subquestId,
      levelKey: continueLevel.levelKey,
    });

    // 4. Navigate to the saved step when available.
    const destinationPath = savedResume
      ? buildLevelResumePath(savedResume)
      : continueLevel.to;

    console.log("[Gwin Resume] Home Continue navigation", {
      levelKey: continueLevel.levelKey,
      resumeFound: !!savedResume,
      savedSegment: savedResume?.resume_segment || null,
      destinationPath,
    });

    navigate(destinationPath);
  } catch (error) {
    console.error(
      "Progress continue or resume check failed:",
      error
    );

    setLevelAccessBlock({
      allowed: false,
      reason: "access_check_failed",
      title: "We could not check your progress",
      message:
        error?.message ||
        "Please refresh and try again.",
      buttonLabel: "TASKS",
      tasksPath: "/resources?tab=tasks",
    });
  } finally {
    setCheckingContinue(false);
  }
}


  const activePostLevelPopup =
    postLevelPopupQueue[0] ||
    null;

  const canShowPostLevelPopup =
    !!activePostLevelPopup &&
    !showFreeQuestCompleteModal &&
    !showSubscriptionPopup &&
    !levelAccessBlock;

  const visiblePostLevelPopup =
    canShowPostLevelPopup
      ? activePostLevelPopup
      : null;

  const visibleTemplatePopup =
    visiblePostLevelPopup?.type ===
    POST_LEVEL_POPUP_TYPES.TEMPLATE_UNLOCK
      ? visiblePostLevelPopup
      : null;

  const visibleReferralNotice =
    visiblePostLevelPopup?.type ===
    POST_LEVEL_POPUP_TYPES.FREE_REFERRAL_UNLOCK_NOTICE;

  const visibleLearningFeedbackEvent =
    visiblePostLevelPopup?.type ===
    POST_LEVEL_POPUP_TYPES.LEARNING_FEEDBACK
      ? visiblePostLevelPopup.event
      : null;


  const completeActivePostLevelPopup =
    () => {
      if (
        !userId ||
        !activePostLevelPopup
      ) {
        return;
      }

      removePostLevelPopup(
        userId,
        activePostLevelPopup.id
      );

      setPostLevelFeedbackError("");

      setPostLevelPopupQueue(
        getPostLevelPopupQueue(
          userId
        )
      );
    };

  const handlePostLevelFeedbackSubmit =
    async ({
      status,
      difficultyRating = null,
      comment = null,
      metadata = {},
    }) => {
      if (
        !userId ||
        !visibleLearningFeedbackEvent ||
        postLevelFeedbackSaving
      ) {
        return;
      }

      setPostLevelFeedbackSaving(true);
      setPostLevelFeedbackError("");

      try {
        await recordLearningFeedback(
          supabase,
          {
            userId,
            event:
              visibleLearningFeedbackEvent,
            status,
            difficultyRating,
            comment,
            metadata: {
              ...metadata,
              source:
                "progress_dashboard_post_level_popup",
            },
          }
        );

        completeActivePostLevelPopup();
      } catch (error) {
        console.error(
          "Unable to save dashboard feedback:",
          error
        );

        setPostLevelFeedbackError(
          error?.message ||
            "We could not save your feedback. Please try again."
        );
      } finally {
        setPostLevelFeedbackSaving(false);
      }
    };

  const handlePostLevelFeedbackDismiss =
    () => {
      if (
        !visibleLearningFeedbackEvent ||
        postLevelFeedbackSaving
      ) {
        return;
      }

      void handlePostLevelFeedbackSubmit({
        status:
          FEEDBACK_STATUSES.DISMISSED,
        metadata: {
          response_action: "not_now",
        },
      });
    };


    const calendarSetupAction = useMemo(() => {
  return (
    dashboardActions.find((action) => {
      const code =
        action.definition_code ||
        action.code;

      return (
        code === "SYSTEM_CALENDAR_SETUP" ||
        action.action_type === "calendar_setup"
      );
    }) || null
  );
}, [dashboardActions]);

// Legacy monthly, tax, compliance and separate weekly action occurrences
// remain in Supabase for future use, but are not part of this MVP dashboard.
// The only financial goal shown is the separate Weekly Financial Check-In,
// which is driven by the weeklyCheckIn helper and its saved submissions.
const regularDashboardActions = useMemo(() => {
  const getSADate = (value) => {
    if (!value) return "";

    const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Africa/Johannesburg",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(new Date(value));

    const get = (type) =>
      parts.find((part) => part.type === type)?.value;

    return `${get("year")}-${get("month")}-${get("day")}`;
  };

  const today = getSADate(new Date());

  return dashboardActions
    .filter((action) => {
      if (!action.due_at) return false;

      return ![
        "completed",
        "dismissed",
        "not_applicable",
        "delegated",
      ].includes(action.status);
    })
    .map((action) => {
      const dueDate = getSADate(action.due_at);

      const status =
        dueDate < today
          ? "overdue"
          : dueDate === today
          ? "due"
          : "upcoming";

      return {
        ...action,
        status,
      };
    })
    .sort((a, b) => {
      const rank = {
        overdue: 0,
        due: 1,
        upcoming: 2,
      };

      const difference =
        rank[a.status] - rank[b.status];

      if (difference !== 0) return difference;

      return new Date(a.due_at) - new Date(b.due_at);
    })
    .slice(0, 3);
}, [dashboardActions]);

const displayedWeeklyAction = calendarSetupAction;

  const firstName =

    String(

      displayName || "User"

    )

      .trim()

      .split(/\s+/)[0] ||

    "User";



  const completedWeeklyLevelCount =
  weeklyLevels.filter(
    (level) =>
      level?.status === "complete"
  ).length;

const completedGoalCount =
  completedWeeklyLevelCount;

// Only learning levels contribute to the weekly learning progress.
const goalCount = weeklyLevels.length;
const goalProgress = goalCount > 0
  ? Math.round((completedGoalCount / goalCount) * 100)
  : 0;

  return (

    <>

      <AppScreenShell

        showHeader={false}

        showPenguinBackground={false}

        showBottomBar={false}

        maxWidth="max-w-[430px]"

        contentClassName="!px-0 !pt-0"

      >

        <div className="min-h-screen bg-[#F4F4F6]">

          {/* Existing Gwin app header */}

          <div className="bg-white">

            <Banner homeTo="/dashboard" />

          </div>



          {/* HERO */}

          <section className="bg-white px-6 pb-7 pt-8">

            <div className="relative min-h-[150px]">

              <div className="relative z-10 max-w-[225px] pt-6">

                <h1 className="text-[28px] font-black leading-[1.08] tracking-[-0.04em] text-[#111111]">

                  Welcome back,

                  <br />

                  {firstName}

                </h1>



                <p className="mt-2 text-[11px] font-medium leading-relaxed text-[#8A8A8A]">

                  Small steps today.

                  Bigger opportunities

                  tomorrow.

                </p>

              </div>



              <img

                src={

                  PENGUIN_BUILDER_SRC

                }

                alt=""

                aria-hidden="true"

                className="pointer-events-none absolute -bottom-1 right-0 h-[145px] w-[170px] object-contain object-bottom"

                draggable="false"

              />

            </div>



            <DashboardStatStrip

              rank={rank}

              streakValue={

                streakValue

              }

              longestStreak={

                longestStreak

              }

              totalXp={

                totalXp

              }

              xpToNext={

                toNext.needed

              }

            />

          </section>



          {/* WEEKLY GOALS */}

          <div className="px-5 py-8">

            <WeeklyGoalsCard

weeklyCheckInStatus={weeklyCheckInStatus}
previousWeekMissed={previousWeekMissed}
onOpenWeeklyCheckIn={() => {
  setWeeklyCheckInOpen(true);
}}

  weeklyLevels={
    weeklyLevels
  }
  weeklyAction={
    displayedWeeklyAction
  }
  onCompleteWeeklyAction={
    handleCompleteAction
  }
  completingActionId={
    completingActionId
  }
  progress={
    goalProgress
  }
  completedCount={
    completedGoalCount
  }
  totalCount={
    goalCount
  }
  locked={
    continueLevelLocked
  }
  loading={
    loadingStats
  }
  checking={
    checkingContinue ||
    paidJourneyAccessLoading
  }
  onContinue={
    handleContinueClick
  }
  onOpenWeeklyAction={(action) => {
    const route =
      action?.route ||
      action?.metadata?.route;

    if (route) {
      navigate(route);
    }
  }}
/>

          </div>



          {/* UPCOMING ACTIONS */}

          <div className="px-5 pb-6">

            <UpcomingActionsCard
  setupAction={
    calendarSetupAction
  }
  actions={
    regularDashboardActions
  }
  loading={
    dashboardActionsLoading
  }
  onOpenAction={(action) => {
    const route =
      action?.route ||
      action?.metadata?.route;

    if (route) {
      navigate(route);
    }
  }}
  onCompleteAction={
    handleCompleteAction
  }
  completingActionId={
    completingActionId
  }
  onOpenCalendar={() => {
    navigate("/calendar");
  }}
/>

          </div>



          {/* PROGRESS */}

          <div className="px-5 pb-8">

            <ProgressCard

              rank={rank}

              nextRank={

                nextRank

              }

              weeklyCheckInSummary={weeklyCheckInSummary}

              totalXp={

                totalXp

              }

              levelsCompleted={

                levelsCompleted

              }

              totalLevels={

                totalLevels

              }

            />

          </div>

        </div>

      </AppScreenShell>

      {/* Dashboard-only PWA installation banner, above the fixed BottomBar. */}
      <InstallPrompt
        suppressed={
          loadingStats ||
          !userId ||
          weeklyCheckInOpen ||
          !!visiblePostLevelPopup ||
          showFreeQuestCompleteModal ||
          showSubscriptionPopup ||
          !!levelAccessBlock
        }
      />

      <DashboardTemplateUnlockModal
        popup={visibleTemplatePopup}
        onClose={completeActivePostLevelPopup}
      />

      <DashboardReferralUnlockNoticeModal
        open={visibleReferralNotice}
        onClose={completeActivePostLevelPopup}
        onProfile={() => {
          completeActivePostLevelPopup();
          navigate("/profile");
        }}
      />

      <LearningFeedbackModal
        event={visibleLearningFeedbackEvent}
        saving={postLevelFeedbackSaving}
        error={postLevelFeedbackError}
        onSubmit={handlePostLevelFeedbackSubmit}
        onDismiss={handlePostLevelFeedbackDismiss}
      />

      
<WeeklyFinancialCheckIn
  open={weeklyCheckInOpen}
  weekStart={activeWeeklyWeekStart}
  onClose={() => {
    setWeeklyCheckInOpen(false);
  }}
  onSubmitted={() => {
    setRefreshKey((current) => current + 1);
  }}
/>

      <LevelAccessBlockedModal

        open={

          !!levelAccessBlock

        }

        title={

          levelAccessBlock?.title

        }

        message={

          levelAccessBlock?.message

        }

        buttonLabel={

          levelAccessBlock?.buttonLabel ||

          "TASKS"

        }

        onClose={() =>

          setLevelAccessBlock(

            null

          )

        }

        onTasks={() => {

          const path =

            levelAccessBlock?.tasksPath ||

            "/resources?tab=tasks";



          setLevelAccessBlock(

            null

          );



          navigate(path);

        }}

      />



      <FreeQuestCompleteModal

        open={

          showFreeQuestCompleteModal

        }

        onClose={() =>

          setShowFreeQuestCompleteModal(

            false

          )

        }

        onBuy={() => {

          setShowFreeQuestCompleteModal(

            false

          );



          setShowSubscriptionPopup(

            true

          );

        }}

      />



      <SubscriptionPopup
        open={showSubscriptionPopup}
        onClose={() => setShowSubscriptionPopup(false)}
      />

    </>

  );

}



function DashboardStatStrip({
  rank,
  streakValue,
  longestStreak,
  totalXp,
  xpToNext,
}) {
  const RankIcon = rank.icon;

  return (
    <div className="relative z-20 -mt-1 overflow-hidden rounded-[18px] border border-[#D8D8D8] bg-white shadow-sm">
      {/* Rank gets half the row; Streak and XP share the remaining half. */}
      <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 items-center gap-2 border-r border-[#E2E2E2] px-2.5 py-3 sm:px-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[#FF914D] text-white">
            <RankIcon className="h-5 w-5" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-[8px] font-black uppercase text-[#FF6E35]">
              Current rank
            </p>
            <p className="text-[11px] font-black leading-tight text-[#111111] break-words">
              {rank.name}
            </p>
            <p className="mt-0.5 text-[7px] leading-tight text-[#9A9A9A]">
              {xpToNext.toLocaleString()} XP to next rank
            </p>
          </div>
        </div>

        <div className="flex min-w-0 flex-col items-center justify-center border-r border-[#E2E2E2] px-1 py-2 text-center">
          <Flame
            className="h-5 w-5 shrink-0 fill-[#FF7138] text-[#FF7138]"
            strokeWidth={1.6}
          />
          <p className="mt-1 text-[8px] font-black uppercase text-[#FF6E35]">
            Streak
          </p>
          <p className="text-[11px] font-black leading-tight text-[#111111]">
            {streakValue} {streakValue === 1 ? "day" : "days"}
          </p>
          <p className="text-[7px] leading-tight text-[#9A9A9A]">
            Longest {longestStreak}d
          </p>
        </div>

        <div className="flex min-w-0 flex-col items-center justify-center px-1 py-2 text-center">
          <Zap
            className="h-5 w-5 shrink-0 fill-[#FFB448] text-[#FFB448]"
            strokeWidth={1.6}
          />
          <p className="mt-1 text-[8px] font-black uppercase text-[#FF6E35]">
            Total XP
          </p>
          <p className="text-[11px] font-black leading-tight text-[#111111] break-all">
            {totalXp.toLocaleString()}
          </p>
        </div>
      </div>
    </div>
  );
}


function WeeklyGoalsCard({
  weeklyLevels = [],
  progress,
  completedCount,
  totalCount,
  locked,
  loading,
  checking,
  onContinue,
  weeklyAction,
  onCompleteWeeklyAction,
  onOpenWeeklyAction,
  completingActionId,

  // Weekly Financial Check-In
  weeklyCheckInStatus,
  previousWeekMissed,
  onOpenWeeklyCheckIn,
}) {

  const firstIncompleteIndex =
    weeklyLevels.findIndex(
      (level) =>
        level?.status !== "complete"
    );

  const completedTitles = [
    "Completed your first level for the week",
    "Completed your second level for the week",
    "Completed your third level for the week",
  ];

  return (
    <section className="rounded-[25px] border border-[#D9D9D9] bg-white px-5 py-6 shadow-[0_2px_3px_rgba(0,0,0,0.02)]">
      <div className="flex items-start justify-between gap-5">
        <h2 className="text-[21px] font-black tracking-[-0.03em] text-[#161616]">
          Your goals for the week
        </h2>

        <div className="w-[106px] pt-2">
          <div className="h-[8px] overflow-hidden rounded-full bg-[#D6D6D6]">
            <div
              className="h-full rounded-full bg-[#FF914D] transition-all duration-500"
              style={{
                width: `${progress}%`,
              }}
            />
          </div>

          <p className="mt-1 text-center text-[7px] font-medium text-[#989898]">
            {completedCount} of{" "}
            {totalCount} complete
          </p>
        </div>
      </div>

      <div className="mt-7 space-y-3.5">
        {weeklyLevels.map(
          (level, index) => {
            const isComplete =
              level?.status === "complete";

            const isCurrent =
              !isComplete &&
              index ===
                firstIncompleteIndex;

            const key =
              `${level.questId}-${level.subquestId}-${level.levelKey}`;

            if (isComplete) {
              return (
                <GoalRow
                  key={key}
                  type="complete"
                  title={
                    completedTitles[index] ||
                    "Completed a level for the week"
                  }
                  duration="3–5 mins"
                  actionLabel="Complete"
                />
              );
            }

            if (isCurrent) {
              return (
                <GoalRow
                  key={key}
                  type="current"
                  title={
                    loading
                      ? "Loading your next level..."
                      : formatLevelTitle(
                          level
                        )
                  }
                  duration="3–5 mins"
                  actionLabel={
                    checking
                      ? "Checking..."
                      : locked
                      ? "Unlock"
                      : level.status ===
                        "in_progress"
                      ? "Continue"
                      : "Start"
                  }
                  clickable
                  disabled={
                    loading || checking
                  }
                  onClick={
                    onContinue
                  }
                />
              );
            }

            return (
              <GoalRow
                key={key}
                type="next"
                title={
                  formatLevelTitle(
                    level
                  )
                }
                duration="3–5 mins"
                actionLabel="Available next"
              />
            );
          }
        )}

        
<GoalRow
  type={
    weeklyCheckInStatus === "completed"
      ? "complete"
      : weeklyCheckInStatus === "upcoming" ||
        weeklyCheckInStatus === "missed"
      ? "calendar"
      : "current"
  }
  title="Weekly Financial Check-In"
  duration="Friday to Wednesday"
  actionLabel={
    weeklyCheckInStatus === "completed"
      ? "Completed"
      : weeklyCheckInStatus === "missed"
      ? "Missed"
      : weeklyCheckInStatus === "upcoming"
      ? "Upcoming Friday"
      : weeklyCheckInStatus === "due"
      ? "Check in"
      : weeklyCheckInStatus === "overdue"
      ? "Check in"
      : weeklyCheckInStatus === "loading"
      ? "Loading..."
      : weeklyCheckInStatus === "error"
      ? "Retry"
      : "Check in"
  }
  clickable={
    weeklyCheckInStatus === "due" ||
    weeklyCheckInStatus === "overdue" ||
    weeklyCheckInStatus === "error"
  }
  disabled={weeklyCheckInStatus === "loading"}
  onClick={onOpenWeeklyCheckIn}
/>

{previousWeekMissed && (
  <GoalRow
    type="calendar"
    title="Last week's financial check-in was missed"
    duration="Previous week"
    actionLabel="Missed"
  />
)}


        {weeklyAction && (
  <GoalRow
    type={
      weeklyAction.action_type === "calendar_setup"
        ? "calendar"
        : "current"
    }
    title={
      weeklyAction.title ||
      "Set up your financial calendar"
    }
    duration={
      weeklyAction.action_type === "calendar_setup"
        ? "3–5 mins"
        : "Weekly action"
    }
    actionLabel={
      weeklyAction.action_type === "calendar_setup"
        ? "Set up"
        : completingActionId === weeklyAction.id
        ? "Saving..."
        : weeklyAction.status === "overdue"
        ? "Overdue"
        : weeklyAction.status === "due"
        ? "Due"
        : "Complete"
    }
    clickable
    disabled={
      weeklyAction.action_type !== "calendar_setup" &&
      completingActionId === weeklyAction.id
    }
    onClick={() => {
      if (weeklyAction.action_type === "calendar_setup") {
        onOpenWeeklyAction?.(weeklyAction);
        return;
      }

      onCompleteWeeklyAction?.(weeklyAction);
    }}
  />
)}
      </div>
    </section>
  );
}



function formatLevelTitle(

  level

) {

  if (!level) {

    return "Your next level";

  }



  const number =

    parseLevelIndex(

      level.levelKey

    );



  if (

    Number.isFinite(number) &&

    number !== 999

  ) {

    return `Level ${number}: ${level.title}`;

  }



  return level.title;

}



function GoalRow({

  type,

  title,

  duration,

  actionLabel,

  clickable = false,

  disabled = false,

  onClick,

}) {

  const config = {

    complete: {
  iconBg: "bg-[#7ED957]",
  border: "border-[#7ED957]",
  icon: (
    <Check className="h-4 w-4 text-white" />
  ),
  pill: "bg-[#7ED957] text-white",
},



    current: {

      iconBg:

        "bg-[#FFC9A9]",

      border:

        "border-[#FF914D]",

      icon: (

        <ArrowRight className="h-4 w-4 text-[#FF914D]" />

      ),

      pill:

        "bg-[#FFD8BF] text-[#FF7A31]",

    },



    next: {

      iconBg:

        "bg-[#C7C7C7]",

      border:

        "border-[#DADADA]",

      icon: (

        <Lock className="h-3.5 w-3.5 text-white" />

      ),

      pill:

        "bg-transparent text-[#8C8C8C]",

    },



    calendar: {

      iconBg:

        "bg-[#C7C7C7]",

      border:

        "border-[#DADADA]",

      icon: (

        <CalendarDays className="h-4 w-4 text-white" />

      ),

      pill:

        "bg-[#FFB8BA] text-[#FF555D]",

    },

  };



  const selected =

    config[type] ||

    config.next;



  const content = (

    <>

      <div

        className={[

          "flex h-9 w-9 shrink-0 items-center justify-center rounded-full",

          selected.iconBg,

        ].join(" ")}

      >

        {selected.icon}

      </div>



      <div

        className={[

          "flex min-h-[58px] min-w-0 flex-1 items-center rounded-[15px] border bg-white px-4 py-2",

          selected.border,

        ].join(" ")}

      >

        <div className="min-w-0 flex-1 pr-3">

          <p className="line-clamp-2 text-[13px] font-black leading-[1.2] text-[#222222]">

            {title}

          </p>



          <span className="mt-1 inline-flex rounded-md bg-[#EEEEEE] px-2 py-0.5 text-[7px] font-bold text-[#8E8E8E]">

            {duration}

          </span>

        </div>



        <span

          className={[

            "shrink-0 rounded-lg px-3 py-1.5 text-[8px] font-bold",

            selected.pill,

          ].join(" ")}

        >

          {actionLabel}

        </span>

      </div>

    </>

  );



  if (clickable) {

    return (

      <button

        type="button"

        onClick={onClick}

        disabled={disabled}

        className="flex w-full items-center gap-3 text-left transition active:scale-[0.995] disabled:opacity-60"

      >

        {content}

      </button>

    );

  }



  return (

    <div className="flex items-center gap-3">

      {content}

    </div>

  );

}



function UpcomingActionsCard({
  setupAction,
  actions = [],
  loading = false,
  onOpenAction,
  onCompleteAction,
  completingActionId,
  onOpenCalendar,
}) {
  const visibleActions = [];

  if (setupAction) {
    visibleActions.push({
      ...setupAction,
      id:
        setupAction.id ||
        "calendar-setup",

      displayStatus:
        "Set up",

      displaySubtitle:
        "Financial calendar",

      isSetup:
        true,
    });
  }

  const activeActions = actions
    .filter((action) => ["overdue", "due", "upcoming"].includes(action.status))
    .filter((action) => {
      if (!action.available_from) return true;
      const available = new Date(action.available_from).getTime();
      return !Number.isFinite(available) || available <= Date.now();
    })
    .sort((a, b) => {
      const rank = { overdue: 0, due: 1, upcoming: 2 };
      const difference = rank[a.status] - rank[b.status];
      if (difference !== 0) return difference;
      const aDue = a.due_at ? new Date(a.due_at).getTime() : Infinity;
      const bDue = b.due_at ? new Date(b.due_at).getTime() : Infinity;
      if (aDue !== bDue) return aDue - bDue;
      return String(a.title || "").localeCompare(String(b.title || ""));
    });

  activeActions.slice(0, setupAction ? 2 : 3).forEach((action) => {
    visibleActions.push({
      ...action,
      displayStatus: action.status === "overdue"
        ? "Overdue"
        : action.status === "due" ? "Due" : "Upcoming",
      displaySubtitle: action.category || "Money routine",
      isSetup: false,
    });
  });

  function getDateParts(value) {
    if (!value) return { date: "—", month: "" };
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: "—", month: "" };
    const parts = new Intl.DateTimeFormat("en-ZA", {
      timeZone: "Africa/Johannesburg",
      day: "2-digit",
      month: "short",
    }).formatToParts(date);
    return {
      date: parts.find((part) => part.type === "day")?.value || "—",
      month: (parts.find((part) => part.type === "month")?.value || "").toUpperCase(),
    };
  }

  return (
    <section className="rounded-[25px] border border-[#D9D9D9] bg-white px-6 py-6">
      <div className="flex items-center justify-between">
        <h2 className="text-[21px] font-black tracking-[-0.03em] text-[#151515]">
          Your Upcoming Actions
        </h2>
      </div>

      {loading ? (
        <p className="mt-6 text-[11px] font-medium text-[#898989]">
          Loading your actions...
        </p>
      ) : visibleActions.length === 0 ? (
        <div className="mt-6 rounded-[16px] bg-[#F7F7F7] px-4 py-4">
          <p className="text-[11px] font-black text-[#222222]">
            You're all caught up.
          </p>

          <p className="mt-1 text-[8px] font-medium leading-relaxed text-[#898989]">
            New money and compliance actions will
            appear here when they are due.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-5">
          {visibleActions.map(
            (action, index) => {
              const dateParts =
                getDateParts(
                  action.due_at
                );

              
              return (
                <div
  key={
    action.id ||
    `${action.title}-${index}`
  }
  className="flex w-full items-center gap-3 text-left"
>
                  <div
                    className={[
                      "flex h-[31px] w-[31px] shrink-0 flex-col items-center justify-center rounded-[8px] leading-none",
                      action.isSetup
                        ? "bg-[#FFF0E6]"
                        : "bg-[#F1F1F3]",
                    ].join(" ")}
                  >
                    {action.isSetup ? (
                      <CalendarDays className="h-4 w-4 text-[#FF914D]" />
                    ) : (
                      <>
                        <span className="text-[9px] font-black text-[#333333]">
                          {
                            dateParts.date
                          }
                        </span>

                        <span className="mt-0.5 text-[6px] font-bold text-[#777777]">
                          {
                            dateParts.month
                          }
                        </span>
                      </>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-black text-[#222222]">
                      {action.title}
                    </p>

                    <div className="mt-1 flex flex-wrap items-center gap-2">
  <span className="text-[8px] text-[#898989]">
    {action.displaySubtitle}
  </span>

  {!action.isSetup && (
    <span
      className={[
        "rounded-md px-2 py-1 text-[8px] font-bold",
        action.status === "overdue"
          ? "bg-red-100 text-red-700"
          : action.status === "due"
          ? "bg-orange-100 text-orange-700"
          : "bg-gray-100 text-gray-600",
      ].join(" ")}
    >
      {action.displayStatus}
    </span>
  )}
</div>
                  </div>

                  {action.isSetup ? (
  <button
    type="button"
    onClick={() => {
      onOpenAction?.(
        action
      );
    }}
    className="min-w-[80px] rounded-lg bg-[#FFD0B7] px-3 py-1.5 text-center text-[8px] font-bold text-[#FF7B3E]"
  >
    Set up
  </button>
) : (
  <button
    type="button"
    disabled={
      completingActionId === action.id
    }
    onClick={(event) => {
      event.stopPropagation();

      onCompleteAction?.(
        action
      );
    }}
    className="min-w-[80px] rounded-lg bg-[#FFD8BF] px-3 py-1.5 text-center text-[8px] font-bold text-[#FF7A31] transition-colors hover:bg-[#FFC9A9] disabled:opacity-60"
  >
    {completingActionId ===
    action.id
      ? "Saving..."
      : "Mark complete"}
  </button>
)}
                </div>
              );
            }
          )}
        </div>
      )}

      <button
        type="button"
        onClick={
          onOpenCalendar
        }
        className="mt-7 inline-flex items-center gap-2 text-[10px] font-bold text-[#FF7740]"
      >
        View full month calendar

        <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </section>
  );
}




function ProgressCard({
  rank,
  nextRank,
  levelsCompleted,
  totalLevels,
  weeklyCheckInSummary,
}) {


  const levelProgress =

    totalLevels > 0

      ? Math.round(

          (levelsCompleted /

            totalLevels) *

            100

        )

      : 0;



  return (

    <section className="rounded-[25px] border border-[#D9D9D9] bg-white px-6 py-7">

      <h2 className="text-[21px] font-black tracking-[-0.03em] text-[#151515]">

        Your progress

      </h2>



      <div className="mt-5">

        <p className="text-[9px] font-black uppercase text-[#FF7138]">

          Current rank

        </p>



        <p className="mt-0.5 text-[17px] font-black leading-tight text-[#111111]">

          {rank.name}

        </p>



        <p className="mt-1 max-w-[270px] text-[9px] font-medium leading-relaxed text-[#8A8A8A]">

          {rank.tagline}

        </p>

      </div>



      <div className="relative mt-7 min-h-[132px]">

        <div className="relative z-10 max-w-[210px]">

          <p className="text-[10px] font-black text-[#222222]">

            What am I getting

            stronger with?

          </p>



          <ul className="mt-1 space-y-0.5 pl-4 text-[9px] font-medium leading-[1.4] text-[#888888]">

            <li className="list-disc">

              Money review routine

            </li>

            <li className="list-disc">

              Records

            </li>

            <li className="list-disc">

              Financial separation

            </li>

            <li className="list-disc">

              Supporting evidence

            </li>

            <li className="list-disc">

              Reconciliation

            </li>

          </ul>

        </div>



        <img

          src={

            PENGUIN_BUILDER_SRC

          }

          alt=""

          aria-hidden="true"

          className="pointer-events-none absolute -bottom-1 right-0 h-[135px] w-[135px] object-contain"

          draggable="false"

        />

      </div>



      <div className="mt-6 grid grid-cols-3 gap-3">

        <ProgressRing

          label="Levels complete"

          value={`${levelsCompleted}/${totalLevels}`}

          progress={

            levelProgress

          }

        />



        
<ProgressRing
  label="Check-ins complete"
  value={`${weeklyCheckInSummary.completed}/${weeklyCheckInSummary.total}`}
  progress={weeklyCheckInSummary.progress}
/>




        <ProgressRing
          label="Actions complete"
          value="—"
          progress={0}
        />

      </div>



      <div className="mt-6">

        <p className="text-[8px] font-black uppercase text-[#FF7138]">

          Next rank

        </p>



        <p className="mt-1 text-[15px] font-black text-[#111111]">

          {nextRank

            ? nextRank.name

            : "Top rank reached"}

        </p>



        {nextRank && (

          <p className="mt-1 text-[8px] font-medium leading-relaxed text-[#8A8A8A]">

            {nextRank.tagline}

          </p>

        )}

      </div>

    </section>

  );

}



function ProgressRing({

  label,

  value,

  progress = 0,

}) {

  const safeProgress =

    Math.max(

      0,

      Math.min(

        100,

        Number(progress) || 0

      )

    );



  return (

    <div className="text-center">

      <p className="mb-2 text-[7px] font-black uppercase text-[#FF7138]">

        {label}

      </p>



      <div

        className="mx-auto flex h-[64px] w-[64px] items-center justify-center rounded-full"

        style={{

          background: `conic-gradient(

            #FF914D 0% ${safeProgress}%,

            #B7B7B7 ${safeProgress}% 100%

          )`,

        }}

      >

        <div className="flex h-[45px] w-[45px] items-center justify-center rounded-full bg-white">

          <span className="text-[15px] font-black text-[#222222]">

            {value}

          </span>

        </div>

      </div>

    </div>

  );

}



function DashboardTemplateUnlockModal({
  popup,
  onClose,
}) {
  const resource = popup?.resource;

  if (!resource) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/45 px-4">
      <div className="relative w-full max-w-[340px] rounded-[24px] bg-white px-6 py-7 text-center shadow-2xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-[#F3F3F3] text-xl font-bold leading-none text-[#757575] hover:bg-[#E8E8E8]"
          aria-label="Close template unlock popup"
        >
          ×
        </button>

        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF1E8] text-3xl">
          📄
        </div>

        <h2 className="mt-4 text-[24px] font-bold leading-tight text-[#333333]">
          New template unlocked
        </h2>

        <p className="mt-2 text-[14px] leading-relaxed text-[#757575]">
          You unlocked{" "}
          <span className="font-bold text-[#333333]">
            {resource.title || "a new template"}
          </span>
          . You can open it now or find it again in your Resources section.
        </p>

        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-5 flex h-[40px] w-full items-center justify-center rounded-[10px] bg-[#FF914D] px-4 text-[15px] font-bold text-white hover:bg-[#E9782F]"
        >
          {resource.label || "OPEN TEMPLATE"}
        </a>
      </div>
    </div>
  );
}

function DashboardReferralUnlockNoticeModal({
  open,
  onClose,
  onProfile,
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[85] flex items-center justify-center bg-black/45 px-4">
      <div className="relative w-full max-w-[340px] rounded-[24px] bg-white px-6 py-7 text-center shadow-2xl">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF1E8] text-3xl">
          🎁
        </div>

        <h2 className="mt-4 text-[24px] font-bold leading-tight text-[#333333]">
          Unlock the 60-Day Challenge for free
        </h2>

        <p className="mt-3 text-[14px] leading-relaxed text-[#757575]">
          Invite 5 users to Gwin and you can unlock the full 60-Day Challenge
          for free.
        </p>

        <div className="mt-4 rounded-2xl border border-[#FFD7BF] bg-[#FFF7F2] px-4 py-3 text-left">
          <p className="text-[13px] font-bold text-[#333333]">
            How to invite users
          </p>

          <p className="mt-1 text-[12px] leading-relaxed text-[#757575]">
            Go to your Profile page to find your referral code and invite link.
            Share your code with other SMMEs or entrepreneurs. Once 5 invited
            users register and use the app, your paid journey access will unlock
            for free.
          </p>
        </div>

        <button
          type="button"
          onClick={onProfile}
          className="mt-5 flex h-[40px] w-full items-center justify-center rounded-[10px] bg-[#FF914D] px-4 text-[15px] font-bold text-white hover:bg-[#E9782F]"
        >
          GO TO PROFILE
        </button>

        <button
          type="button"
          onClick={onClose}
          className="mt-3 flex h-[38px] w-full items-center justify-center rounded-[10px] border border-[#E5E5E5] bg-white px-4 text-[14px] font-bold text-[#555555] hover:bg-[#F7F7F7]"
        >
          NOT NOW
        </button>
      </div>
    </div>
  );
}

function FreeQuestCompleteModal({

  open,

  onClose,

  onBuy,

}) {

  if (!open) {

    return null;

  }



  return (

    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 px-4">

      <div className="w-full max-w-sm rounded-3xl bg-white p-5 text-center shadow-2xl">

        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#FFF4EC] text-3xl">

          🎉

        </div>



        <h2 className="mt-4 text-xl font-black leading-tight text-[#333333]">

          Continue your Gwin

          journey

        </h2>



        <p className="mt-3 text-sm font-bold leading-relaxed text-[#757575]">

          Your free access has ended.

          Subscribe to continue learning.

        </p>



        <button

          type="button"

          onClick={onBuy}

          className="mt-5 inline-flex h-12 w-full items-center justify-center rounded-2xl bg-[#FF914D] px-5 text-sm font-black text-white"

        >

          Subscribe

        </button>



        <button

          type="button"

          onClick={onClose}

          className="mt-3 inline-flex h-12 w-full items-center justify-center rounded-2xl border border-[#FF914D]/30 bg-white px-5 text-sm font-black text-[#FF914D]"

        >

          Not now

        </button>

      </div>

    </div>

  );

}